import { getLang, LooseObject } from '@gif/shared-global';

/**
 * This function will attempt to replace strings with another string or a link, depending on the format.
 * If you need to add a click listener, you're better off breaking apart the text.
 *
 * Example:
 *  dictionary string = 'This is an %example with a {{[linkExample]:Link text to display]}}'
 *
 * replaceMap = {
 *  example: 'exampleUpdated',
 *  linkExample: 'https://example.com/',
 * }
 *
 * outputs: 'This is an exampleUpdated with a <a href='https://example.com/'>Link text to display</a>
 */
export const translate = (
  key: string,
  replaceMap: LooseObject = {},
  lang: string = getLang()
) => {
  let output: string = dictionary[key][lang];
  for (const r in replaceMap) {
    // this searches for alphanumeric, its accented variants, `-`, `_`, `.`, and space
    const matches = output.match(/{{\[\w+\]:([A-Za-zÀ-ÖØ-öø-ÿ0-9_\-.\s]+)}}/);
    if (matches) {
      output = output.replace(
        `{{[${r}]:${matches[1]}}}`,
        `<a href="${replaceMap[r]}">${matches[1]}</a>`
      );
    } else {
      output = output.replace('%' + r, replaceMap[r]);
    }
  }
  return output;
};

export const dictionary: LooseObject = {
  nickname: {
    en: 'Nickname',
    es: 'Nombre de usuario',
  },
  nicknameLengthError: {
    en: 'Nickname must be 6-15 characters long',
    es: 'El nombre de usuario debe tener entre 6 y 15 caracteres',
  },
  nicknameAlphanumericError: {
    en: 'Nickname must contain only letters and numbers',
    es: 'El nombre de usuario solo puede contener letras y números',
  },
  chooseAnotherNickname: {
    en: 'Please choose another nickname',
    es: 'Por favor, elige otro nombre de usuario',
  },
  dateOfBirth: {
    // This differs from mobile due to how DoB inputs are implemented on web
    en: 'Date of Birth',
    es: 'Fecha de Nacimiento',
  },
  MM: {
    en: 'MM',
    es: 'MM',
  },
  DD: {
    en: 'DD',
    es: 'DD',
  },
  YYYY: {
    en: 'YYYY',
    es: 'AAAA',
  },
  enterValidDate: {
    en: 'Please enter a valid date',
    es: 'Por favor, introduce una fecha válida',
  },
  enterValidMonth: {
    en: 'Please enter a valid month',
    es: 'Por favor, introduzca un mes válido',
  },
  enterValidDay: {
    en: 'Please enter a valid day',
    es: 'Por favor, ingresa un día válido',
  },
  enterValidYear: {
    en: 'Please enter a valid year',
    es: 'Por favor, introduzca una fecha válida',
  },
  minMaxAgeError: {
    en: 'Age must be minimum of %min years and maximum of %max years',
    es: 'La edad debe ser mínima de %min años y máxima de %max años',
  },
  countryOfResidence: {
    en: 'Country of Residence',
    es: 'País de Residencia',
  },
  countryNotListed: {
    en: 'If your country is not listed, choose "Other"',
    es: 'Si tu país no está en la lista, elige "Otro"',
  },
  countryIsRequired: {
    en: 'Country is required',
    es: 'Se requiere país',
  },
  province: {
    en: 'Province',
    es: 'Provincia',
  },
  provinceIsRequired: {
    en: 'Province is required',
    es: 'Se requiere provincia',
  },
  additionalInformation: {
    en: 'Additional Information',
    es: 'Información adicional',
  },
  updateInformation: {
    en: 'Update Information',
    es: 'Actualizar información',
  },
  updateAccountToS: {
    en: "By updating your account you agree to MLB.com's {{[tos]:Terms of Service}} and {{[privacy]:Privacy Policy}}.",
    es: 'Al actualizar tu cuenta, aceptas los {{[tosES]:Términos de Servicio}} y la {{[privacyES]:Política de Privacidad}} de MLB.com.',
  },
  somethingWentWrong: {
    en: 'Oops! Something went wrong.',
    es: '¡Ups! Algo salió mal.',
  },
  pageNotLoading: {
    en: 'Error: Page Not Loading',
    es: 'Error: Página no cargada.',
  },
  tryReloadingPage: {
    en: "We're sorry for the inconvenience. Please try reloading the page.",
    es: 'Lo sentimos por las molestias. Por favor, intenta recargar la página.',
  },
  dismissAgeRestrictionModal: {
    en: 'Dismiss age restriction modal',
    es: 'Descartar modo de restricción de edad',
  },
  doNotMeetAgeRequirements: {
    en: 'You currently do not meet the age requirements',
    es: 'Actualmente no cumples con los requisitos de edad',
  },
  mustBeMinAgeToPlay: {
    en: 'You must be at least %min years of age to play %game. Feel free to participate in our other great MLB Play Games.',
    es: 'Debes tener al menos %min años de edad para jugar %game. Siéntete libre de participar en nuestros otros geniales juegos de MLB Play.',
  },
  dismissLoginMessageModal: {
    en: 'Dismiss login modal',
    es: 'Descartar modo de inicio de sesión',
  },
  pleaseLogInOrCreateAccount: {
    en: 'Please Log In or Create Account',
    es: 'Por favor, inicia sesión o crea una cuenta',
  },
  mustLogInToAccessGame: {
    en: 'You must log in or create a new MLB account to access %game.',
    es: 'Debes iniciar sesión o crear una nueva cuenta de MLB para acceder a %game.',
  },
  mustBeOverMinAge: {
    en: 'You must be over %min+ years old to play',
    es: 'Debes tener más de %min años para jugar',
  },
  logInToMLB: {
    en: 'Log in to MLB.com',
    es: 'Iniciar sesión en la MLB.com',
  },
  dismissUpdateAccountModal: {
    en: 'Dismiss update account information modal',
    es: 'Descartar modo de actualización de información de la cuenta',
  },
  updateAccountInformation: {
    en: 'Update Account Information',
    es: 'Actualizar información de la cuenta',
  },
  pleaseUpdateYourAccount: {
    en: 'Please update your account information in order to play %game.',
    es: 'Por favor, actualiza la información de tu cuenta para jugar %game.',
  },
  updateMyAccount: {
    en: 'Update My Account',
    es: 'Actualizar mi cuenta',
  },
  mlbPlayLetsYouEnjoyFreeToPlayGames: {
    en: 'MLB Play lets you enjoy free-to-play games like Beat the Streak, MLB Pickle, Bingo and By The Numbers, with new games and features introduced year round.',
    es: 'MLB Play te permite disfrutar de juegos gratuitos como Beat the Streak, MLB Pickle, Bingo y By The Numbers, con nuevos juegos y características presentadas durante todo el año',
  },
  backgroundScrim: {
    en: 'Background Scrim',
    es: 'Fondo de pantalla',
  },
  readyToShareTheFun: {
    en: 'Ready to share the fun!',
    es: 'Listo para compartir la diversión!',
  },
  sharingThisLinkAddsMembers: {
    en: 'Sharing this link adds members to %game.',
    es: 'Compartir este enlace para agregar miembros a %game.',
  },
  guessTodaysMysteryMLBPlayer: {
    en: "Guess today's mystery MLB player within nine guesses.",
    es: 'Adivina el jugador misterioso de MLB de hoy en nueve intentos.',
  },
  weDroppedTheBall: {
    en: 'OOF! We dropped the ball',
    es: '¡Ay! Dejamos caer la bola',
  },
  youHaveReachedThisPageInError: {
    en: 'We are sorry, but you have reached this page in error.',
    es: 'Lo sentimos, pero has llegado a esta página por error.',
  },
  pleaseTryTheActionAgain: {
    en: 'Please try the action again or go to the',
    es: 'Por favor, intenta la acción de nuevo o ve al',
  },
  ifTheProblemContinues: {
    en: 'If the problem continues, contact',
    es: 'Si el problema persiste, contacta al',
  },
  '404NotFound': {
    en: '404 Not Found',
    es: '404 No Encontrado',
  },
  day: {
    en: 'Day',
    es: 'Día',
  },
  month: {
    en: 'Month',
    es: 'Mes',
  },
  year: {
    en: 'Year',
    es: 'Año',
  },
  country: {
    en: 'Country',
    es: 'País',
  },
  allGames: {
    en: 'All Games',
    es: 'Todos los juegos',
  },
  reloadPage: {
    en: 'Reload Page',
    es: 'Recargar página',
  },
  close: {
    en: 'Close',
    es: 'Cerrar',
  },
  presented: {
    en: 'presented',
    es: 'presentado',
  },
  by: {
    en: 'by',
    es: 'por',
  },
  returnToMLBPlay: {
    en: 'Return to MLB Play',
    es: 'Volver a MLB Play',
  },
  allFieldsRequired: {
    en: 'All fields required',
    es: 'Todos los campos requeridos',
  },
  troubleshoot: {
    en: 'Troubleshoot',
    es: 'Solucionar problemas',
  },
  clickToCopyLink: {
    en: 'Click to copy link',
    es: 'Haz clic para copiar el enlace',
  },
  noGamesAvailable: {
    en: 'There are no games available at the moment. Please check back in later!',
    es: 'Actualmente no hay juegos disponibles. ¡Por favor, vuelve más tarde!',
  },
  gameHub: {
    en: 'Game Hub',
    es: 'Centro de Juegos',
  },
  customerSupport: {
    en: 'Customer Support',
    es: 'Soporte al Cliente',
  },
  logIn: {
    en: 'Log In',
    es: 'Remitir',
  },
  logInTitle: {
    en: 'Log In',
    es: 'Conectarte',
  },
  email: {
    en: 'Email',
    es: 'Correo Electrónico',
  },
  password: {
    en: 'Password',
    es: 'Contraseña',
  },
  needHelpLoggingIn: {
    en: 'Need help logging in?',
    es: '¿Necesitas ayuda para conectarte?',
  },
  signUp: {
    en: 'Sign Up',
    es: 'Crear Cuenta',
  },
  dontHaveAnAccount: {
    en: "Don't have an account?",
    es: '¿No tienes cuenta?',
  },
  thereWasAnError: {
    en: 'There was an error. Please try again',
    es: 'Hubo un error. Favor de intentar de nuevo',
  },
  required: {
    en: 'required',
    es: 'requerido',
  },
  createPassword: {
    en: 'Create Password',
    es: 'Crear tu contraseña',
  },
  confirmPassword: {
    en: 'Confirm Password',
    es: 'Confirma Contraseña',
  },
  receiveCommercialEmails: {
    en: 'I would like to receive commercial e-mails from <a href="http://mlb.com" target="_blank">MLB.com</a> and their partners.',
    es: 'Me gustaría recibir correos electrónicos de <a href="http://mlb.com" target="_blank">MLB.com</a> y sus socios.',
  },
  register: {
    en: 'Register',
    es: 'Crear Cuenta',
  },
  alreadyHaveAnAccount: {
    en: 'Already have an account?',
    es: '¿Ya tienes una cuenta?',
  },
  agreeToToS: {
    en: 'I understand and agree to be bound by the MLB.com <a href="/official-information/terms-of-use" target="_blank">Terms of Use</a> and <a href="/official-information/privacy-policy" target="_blank">Privacy Policy</a>.',
    es: 'Entiendo que estoy obligado a regirme por el <a href="/es/official-information/terms-of-use" target="_blank">Reglamento de Términos</a> y <a href="/es/official-information/privacy-policy" target="_blank">Uso de Privacidad</a> de MLB.com',
  },
};
